import { useEffect, useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { supabase } from '../../lib/supabase';
import { AdminPageHeader } from '../../components/admin/AdminPageHeader';

type Period = '7d' | '30d' | '90d';
const PERIODS: { key: Period; label: string }[] = [
  { key: '7d', label: '7 dias' },
  { key: '30d', label: '30 dias' },
  { key: '90d', label: '90 dias' },
];

interface FeatureStat {
  feature: string;
  calls: number;
  cacheHits: number;
  cacheRate: number;
  tokensIn: number;
  tokensOut: number;
  costUsd: number;
}

interface DailyStat {
  date: string;
  calls: number;
  costUsd: number;
}

export function AdminApiUsagePage() {
  const [period, setPeriod] = useState<Period>('30d');
  const [features, setFeatures] = useState<FeatureStat[]>([]);
  const [daily, setDaily] = useState<DailyStat[]>([]);
  const [totals, setTotals] = useState({ calls: 0, costUsd: 0, cacheRate: 0 });
  const [loading, setLoading] = useState(false);

  useEffect(() => { load(); }, [period]);

  function getDaysAgo(d: number) {
    return new Date(Date.now() - d * 86400000).toISOString();
  }

  const daysMap: Record<Period, number> = { '7d': 7, '30d': 30, '90d': 90 };

  async function load() {
    setLoading(true);
    const since = getDaysAgo(daysMap[period]);
    const { data } = await supabase
      .from('api_usage_log')
      .select('feature, cache_hit, tokens_in, tokens_out, cost_usd, created_at')
      .gte('created_at', since)
      .order('created_at', { ascending: true });

    if (!data) { setLoading(false); return; }

    // Per-feature aggregation
    const featureMap: Record<string, FeatureStat> = {};
    const dailyMap: Record<string, DailyStat> = {};

    data.forEach(row => {
      const f = row.feature ?? 'unknown';
      if (!featureMap[f]) featureMap[f] = { feature: f, calls: 0, cacheHits: 0, cacheRate: 0, tokensIn: 0, tokensOut: 0, costUsd: 0 };
      featureMap[f].calls++;
      if (row.cache_hit) featureMap[f].cacheHits++;
      featureMap[f].tokensIn  += row.tokens_in  ?? 0;
      featureMap[f].tokensOut += row.tokens_out ?? 0;
      featureMap[f].costUsd   += row.cost_usd   ?? 0;

      const date = (row.created_at as string).slice(0, 10);
      if (!dailyMap[date]) dailyMap[date] = { date, calls: 0, costUsd: 0 };
      dailyMap[date].calls++;
      dailyMap[date].costUsd += row.cost_usd ?? 0;
    });

    const featureList = Object.values(featureMap).map(f => ({
      ...f,
      cacheRate: f.calls > 0 ? Math.round((f.cacheHits / f.calls) * 100) : 0,
    })).sort((a, b) => b.costUsd - a.costUsd);

    const dailyList = Object.values(dailyMap).sort((a, b) => a.date.localeCompare(b.date));

    const totalCalls   = featureList.reduce((s, f) => s + f.calls, 0);
    const totalCost    = featureList.reduce((s, f) => s + f.costUsd, 0);
    const totalHits    = featureList.reduce((s, f) => s + f.cacheHits, 0);
    const totalCacheRate = totalCalls > 0 ? Math.round((totalHits / totalCalls) * 100) : 0;

    setFeatures(featureList);
    setDaily(dailyList);
    setTotals({ calls: totalCalls, costUsd: totalCost, cacheRate: totalCacheRate });
    setLoading(false);
  }

  return (
    <div>
      <AdminPageHeader
        title="Uso de IA (API)"
        objective="Custo e volume de chamadas à inteligência artificial (Claude e outros modelos). Cache Hit Rate = % de respostas servidas do cache sem chamar a IA, quanto maior, menor o custo. Monitore para controlar gastos."
      />
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '0.75rem' }}>
        <div style={{ display: 'flex', gap: '0.35rem' }}>
          {PERIODS.map(p => (
            <button key={p.key} onClick={() => setPeriod(p.key)}
              className={period === p.key ? 'btn btn-primary btn-sm' : 'btn btn-secondary btn-sm'}>
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {/* Summary cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem', marginBottom: '1.5rem' }}>
        <div className="card" style={{ textAlign: 'center' }}>
          <p style={{ fontSize: '1.75rem', fontWeight: 700, color: '#60a5fa', margin: 0 }}>{totals.calls.toLocaleString('pt-BR')}</p>
          <p style={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)', marginTop: '0.25rem' }}>Total de chamadas</p>
        </div>
        <div className="card" style={{ textAlign: 'center' }}>
          <p style={{ fontSize: '1.75rem', fontWeight: 700, color: '#f59e0b', margin: 0 }}>${totals.costUsd.toFixed(4)}</p>
          <p style={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)', marginTop: '0.25rem' }}>Custo total (USD)</p>
          <p style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '0.1rem' }}>(≈ R$ {(totals.costUsd * 5.5).toFixed(2)})</p>
        </div>
        <div className="card" style={{ textAlign: 'center' }}>
          <p style={{ fontSize: '1.75rem', fontWeight: 700, color: totals.cacheRate >= 50 ? '#4ade80' : totals.cacheRate >= 25 ? '#fbbf24' : '#f87171', margin: 0 }}>{totals.cacheRate}%</p>
          <p
            style={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)', marginTop: '0.25rem', borderBottom: '1px dashed var(--petrol)', display: 'inline-block', cursor: 'help' }}
            title="Cache Hit Rate: porcentagem de respostas entregues a partir do cache (sem custo de IA). Ideal: acima de 40%."
          >
            Cache Hit Rate
          </p>
        </div>
      </div>

      {loading ? (
        <div className="card shimmer" style={{ height: '300px' }} />
      ) : (
        <>
          {/* Daily cost chart */}
          <div className="card" style={{ marginBottom: '1.5rem' }}>
            <p style={{ fontWeight: 600, color: 'var(--text-primary)', marginBottom: '1rem' }}>Custo diário (USD)</p>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={daily}>
                <XAxis dataKey="date" tick={{ fontSize: 10, fill: 'var(--text-muted)' }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 10, fill: 'var(--text-muted)' }} axisLine={false} tickLine={false} tickFormatter={v => `$${v.toFixed(3)}`} />
                <Tooltip formatter={(v: number) => [`$${v.toFixed(4)}`, 'Custo']} contentStyle={{ backgroundColor: 'var(--bg-elevated)', border: '1px solid var(--border-subtle)', borderRadius: '6px', fontSize: '12px' }} />
                <Bar dataKey="costUsd" name="Custo USD" fill="#f59e0b" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Per-feature table */}
          <div className="card">
            <p style={{ fontWeight: 600, color: 'var(--text-primary)', marginBottom: '1rem' }}>Custo por funcionalidade</p>
            {features.length === 0 ? (
              <p style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>Nenhum dado encontrado para o período.</p>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table className="data-table" style={{ width: '100%' }}>
                  <thead>
                    <tr>
                      <th>Funcionalidade</th>
                      <th style={{ textAlign: 'right' }}>Chamadas</th>
                      <th style={{ textAlign: 'right' }}>
                        <span
                          title="Cache Hit Rate: porcentagem de respostas entregues a partir do cache (sem custo de IA). Ideal: acima de 40%."
                          style={{ borderBottom: '1px dashed var(--petrol)', cursor: 'help' }}
                        >
                          Cache %
                        </span>
                        <span style={{ display: 'block', fontSize: '10px', fontWeight: 400, color: 'var(--text-muted)', marginTop: '2px' }}>🟢 &gt;50% · 🟡 25-50% · 🔴 &lt;25%</span>
                      </th>
                      <th style={{ textAlign: 'right' }}>
                        <span
                          title="Tokens: unidades de texto processadas pela IA. Tokens de entrada (prompt) + saída (resposta) = custo total."
                          style={{ borderBottom: '1px dashed var(--petrol)', cursor: 'help' }}
                        >
                          Tokens In
                        </span>
                      </th>
                      <th style={{ textAlign: 'right' }}>
                        <span
                          title="Tokens: unidades de texto processadas pela IA. Tokens de entrada (prompt) + saída (resposta) = custo total."
                          style={{ borderBottom: '1px dashed var(--petrol)', cursor: 'help' }}
                        >
                          Tokens Out
                        </span>
                      </th>
                      <th style={{ textAlign: 'right' }}>Custo (USD)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {features.map(f => (
                      <tr key={f.feature}>
                        <td style={{ color: 'var(--text-primary)', fontWeight: 600, fontFamily: 'monospace', fontSize: '12px' }}>{f.feature}</td>
                        <td style={{ textAlign: 'right', color: 'var(--text-secondary)' }}>{f.calls.toLocaleString('pt-BR')}</td>
                        <td style={{ textAlign: 'right' }}>
                          <span style={{ color: f.cacheRate >= 50 ? '#4ade80' : f.cacheRate >= 25 ? '#fbbf24' : '#f87171', fontWeight: 600 }}>
                            {f.cacheRate}%
                          </span>
                        </td>
                        <td style={{ textAlign: 'right', color: 'var(--text-muted)', fontSize: '12px' }}>{f.tokensIn.toLocaleString('pt-BR')}</td>
                        <td style={{ textAlign: 'right', color: 'var(--text-muted)', fontSize: '12px' }}>{f.tokensOut.toLocaleString('pt-BR')}</td>
                        <td style={{ textAlign: 'right', fontWeight: 600, color: f.costUsd > 5 ? '#f87171' : f.costUsd > 1 ? '#fbbf24' : '#4ade80' }}>
                          ${f.costUsd.toFixed(4)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
